import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUniversity, FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaClock, FaCalendarAlt, FaImage, FaList } from 'react-icons/fa';

const UniversityDetailsModal = ({ isOpen, onClose, university }) => {
  if (!university) return null;

  // Function to format text with proper line breaks
  const formatText = (text) => {
    return text ? text.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        <br />
      </span>
    )) : 'N/A';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-5xl w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with university logo and name */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center min-w-0 flex-1">
                {university.logo ? (
                  <img
                    src={university.logo}
                    alt={university.name?.en || 'University Logo'}
                    className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white p-1 mr-3 sm:mr-4 object-contain flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <FaUniversity className="h-5 w-5 sm:h-6 sm:w-6 text-blue-800" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-2xl font-bold truncate">{university.name?.en || 'N/A'}</h2>
                  <p className="text-blue-100 text-xs sm:text-sm truncate">{university.universityId || 'N/A'}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors bg-blue-700 hover:bg-blue-800 p-1.5 sm:p-2 rounded-full flex-shrink-0 ml-2"
              >
                <FaTimes className="h-4 w-4 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Main content with tabs */}
            <div className="p-0 bg-gray-50">
              {/* University image banner if available */}
              {university.image && (
                <div className="w-full h-48 md:h-64 overflow-hidden">
                  <img
                    src={university.image}
                    alt={university.name?.en || 'University'}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Quick Info Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-start">
                    <div className="bg-blue-50 p-3 rounded-lg mr-3">
                      <FaPhone className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">Phone</h4>
                      <p className="text-gray-800">{university.contactInfo?.phone || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-start">
                    <div className="bg-blue-50 p-3 rounded-lg mr-3">
                      <FaEnvelope className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">Email</h4>
                      <p className="text-gray-800 truncate">{university.contactInfo?.email || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-start">
                    <div className="bg-blue-50 p-3 rounded-lg mr-3">
                      <FaMapMarkerAlt className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">Location</h4>
                      <p className="text-gray-800">{university.address?.city?.en || 'N/A'}, {university.address?.country?.en || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Main Content Sections */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left Column */}
                  <div className="md:col-span-2 space-y-6">
                    {/* About Section */}
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                        <FaUniversity className="mr-2 text-blue-700" /> About University
                      </h3>
                      <div className="prose max-w-none text-gray-700">
                        <p className="mb-4">{formatText(university.description?.en)}</p>

                        <h4 className="text-lg font-medium text-blue-800 mt-6 mb-2">Dentistry Program</h4>
                        <p className="mb-4">{formatText(university.program?.en)}</p>

                        <h4 className="text-lg font-medium text-blue-800 mt-6 mb-2">Dentistry Information</h4>
                        <p className="mb-4">{formatText(university.dentistryInfo?.en)}</p>

                        <h4 className="text-lg font-medium text-blue-800 mt-6 mb-2">Facilities</h4>
                        <p>{formatText(university.facilities?.en)}</p>
                      </div>
                    </section>

                    {/* Dentistry Services */}
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                        <FaList className="mr-2 text-blue-700" /> Dentistry Services
                      </h3>
                      {university.dentistryServices?.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {university.dentistryServices.map((service, index) => (
                            <div key={index} className="bg-blue-50 p-3 rounded-lg text-blue-800">
                              <span className="font-medium">{service.en || 'N/A'}</span>
                              <span className="text-sm text-blue-600 block mt-1 rtl:text-right">{service.ar || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 italic">No dentistry services available.</p>
                      )}
                    </section>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                        <FaGlobe className="mr-2 text-blue-700" /> Contact & Links
                      </h3>
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <FaPhone className="text-blue-600 mt-1 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Phone</p>
                            <p className="text-gray-800">{university.contactInfo?.phone || 'N/A'}</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <FaEnvelope className="text-blue-600 mt-1 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Email</p>
                            <p className="text-gray-800 break-all">{university.contactInfo?.email || 'N/A'}</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <FaGlobe className="text-blue-600 mt-1 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Website</p>
                            {university.contactInfo?.website ? (
                              <a
                                href={university.contactInfo.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                Visit Website
                              </a>
                            ) : (
                              <p className="text-gray-500">N/A</p>
                            )}
                          </div>
                        </li>
                        <li className="flex items-start">
                          <FaMapMarkerAlt className="text-blue-600 mt-1 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Address</p>
                            <p className="text-gray-800">
                              {university.address?.street?.en || 'N/A'}, {university.address?.city?.en || 'N/A'}, {university.address?.country?.en || 'N/A'} {university.address?.postalCode || ''}
                            </p>
                          </div>
                        </li>
                      </ul>

                      {university.mapUrl && (
                        <div className="mt-4">
                          <a
                            href={university.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full py-2 px-4 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700 transition-colors mt-2"
                          >
                            View on Map
                          </a>
                        </div>
                      )}
                    </section>

                    {/* Slot Information */}
                    <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
                        <FaClock className="mr-2 text-blue-700" /> Slot Information
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FaCalendarAlt className="text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Slot Period</span>
                          </div>
                          <span className="text-gray-800">
                            {university.slotBeginDate ? new Date(university.slotBeginDate).toLocaleDateString() : 'N/A'} - {university.slotEndDate ? new Date(university.slotEndDate).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FaClock className="text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Slot Duration</span>
                          </div>
                          <span className="text-gray-800">
                            {university.slotDuration ? `${university.slotDuration} minutes` :
                             (university.timeSlots && university.timeSlots.length > 0 && university.timeSlots[0].duration) ?
                             `${university.timeSlots[0].duration} minutes` : 'N/A'}
                          </span>
                        </div>

                        <div>
                          <div className="flex items-center mb-2">
                            <FaClock className="text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Available Time Slots</span>
                          </div>

                          {university.timeSlots && university.timeSlots.length > 0 ? (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {[...new Set(university.timeSlots.map(slot => slot.time))].map((time, index) => (
                                <span key={index} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                                  {time}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">No time slots available.</p>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center mb-2">
                            <FaCalendarAlt className="text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Holidays</span>
                          </div>

                          {university.holidays && university.holidays.length > 0 ? (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {university.holidays.map((day, index) => (
                                <span key={index} className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm">
                                  {day}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">No holidays specified.</p>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white p-4 sm:p-6 border-t rounded-b-xl sm:rounded-b-2xl flex justify-end">
              <button
                onClick={onClose}
                className="px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-lg hover:from-blue-600 hover:to-blue-800 transition-colors shadow-md hover:shadow-lg font-medium text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UniversityDetailsModal;